import { formatTime, formatDate } from '@smarttransit/common'
import { initBreadcrumbs, addAlert, isAuthorized, getCachedRegions } from '@/utilities/helpers'
import { genericApiRequests } from '@/utilities/axios-factory'
import {
  getOcppCharger,
  createOcppCharger,
  updateOcppCharger,
  deleteOcppCharger,
  setChargerCredential,
  clearChargerCredential,
  debugPopulateOcppChargerBootData,
  getOcppChargerTemplate
} from '@/services/ocpp-charger-service'
import {
  getChargerChargingHistory,
  countChargerChargingHistory
} from '@/services/ocpp-assignment-service'
import {
  getBusStopData,
  getCoarseGeolocation,
  googleGeocodeRequest,
  googleReverseGeocodeRequest
} from '@/services/maps-service'
import {
  geocodeSearch,
  reverseGeocodeBusStop
} from '@/services/bus-stops-service'
import OcppEmulatorDialog from '../components/ocpp-emulator-dialog'

const REVERSE_GEOCODE_CACHE_TTL_MS = 30 * 60 * 1000
const BUS_STOP_REVERSE_GEOCODE_RADIUS_METERS = 75
const reverseGeocodeCache = {}

export default {
  name: 'advanced-ev-chargers-charger',
  components: {
    OcppEmulatorDialog
  },
  props: {
    signedInUser: Object,
    chargerId: String
  },
  data () {
    return {
      apiInProgress: false,
      breadcrumbs: [],
      form: Object.assign({}, getOcppChargerTemplate()),
      locationPickerDialog: false,
      locationPickerMapShouldRender: false,
      locationPickerCenter: null,
      locationPickerSelection: null,
      locationPickerMapHeightOffset: 320,
      markerOptions: { startMarkerClass: 'st-map-marker-start', endMarkerClass: 'st-map-marker-end' },
      stMapApis: null,
      mapboxAccessToken: process.env.VUE_APP_MAPBOX_KEY,
      maptilerKey: process.env.VUE_APP_MAPTILER_KEY,
      credentialForm: {
        password: '',
        confirmPassword: '',
        showPassword: false,
        pushToCharger: false,
        passwordTouched: false,
        confirmPasswordTouched: false,
        mismatchAlertShown: false
      },
      activeChargerTab: 0,
      historyRecords: [],
      historyTotalRecords: 0,
      historyLoading: false,
      historyFilters: {
        licensePlate: '',
        dateFrom: '',
        dateTo: ''
      },
      historyPagination: {
        descending: true,
        page: 1,
        rowsPerPage: 10,
        sortBy: 'dateCreated'
      },
      historyHeaders: [
        { text: 'Date Created', value: 'date_created', sortable: true },
        { text: 'Vehicle', value: 'license_plate_number', sortable: false },
        { text: 'idTag', value: 'id_tag', sortable: false },
        { text: 'Start', value: 'start_time', sortable: false },
        { text: 'Stop', value: 'stop_time', sortable: false },
        { text: 'kWh', value: 'energy_delivered_kwh', sortable: false },
        { text: 'Status', value: 'status', sortable: false }
      ],
      emulatorDialogVisible: false
    }
  },
  computed: {
    isNewRecord () {
      return !this.chargerId || this.chargerId === '0'
    },
    isAdmin () {
      return isAuthorized(this.signedInUser, 'admin')
    },
    isStagingOrLocal () {
      return process.env.VUE_APP_ENV !== 'production'
    },
    statusOptions () {
      return [
        'Offline',
        'Online',
        'Available',
        'Preparing',
        'Charging',
        'SuspendedEV',
        'SuspendedEVSE',
        'Finishing',
        'Reserved',
        'Unavailable',
        'Faulted'
      ]
    },
    credentialPasswordErrors () {
      const password = this.credentialForm.password || ''
      if (!this.credentialForm.passwordTouched || !password) {
        return []
      }
      return password.length < 8 ? ['Password must be at least 8 characters'] : []
    },
    credentialConfirmPasswordErrors () {
      const password = this.credentialForm.password || ''
      const confirmPassword = this.credentialForm.confirmPassword || ''
      if (!this.credentialForm.confirmPasswordTouched || !confirmPassword || !password) {
        return []
      }
      return password !== confirmPassword ? ['Passwords do not match'] : []
    },
    canUseEmulator () {
      return this.isAdmin && this.isStagingOrLocal && !this.isNewRecord && !!this.form.chargePointId
    }
  },
  async mounted () {
    this._locationPickerMapboxInstanceWrapper = null
    this._locationPickerPopup = null
    this.stMapApis = {
      genericApiRequests,
      addAlert,
      getBusStops: ({ searchradius, ...rest }) => (getBusStopData({ ...rest, radius: searchradius })),
      getCachedRegions,
      googleGeocodeRequest,
      getCoarseGeolocation,
      geocodeRequest: (keywords, longlat) => geocodeSearch({ keywords, longlat })
    }
    this.breadcrumbs = initBreadcrumbs.bind(this)()
    await this.loadRecord()
  },
  methods: {
    formatDateTime (value, timezone) {
      if (!value) {
        return '-'
      }

      const trimmedTimezone = timezone && String(timezone).trim()
      return `${formatTime(value, trimmedTimezone)}, ${formatDate(value, trimmedTimezone, { displayTimezone: true })}`
    },
    formatKwh (value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      return value + ' kWh'
    },
    buildHistoryOrder () {
      const direction = this.historyPagination.descending ? 'DESC' : 'ASC'
      return 'date_created ' + direction
    },
    onChargerTabChanged (value) {
      this.activeChargerTab = Number(value)
      if (this.activeChargerTab === 1 && !this.isNewRecord) {
        this.refreshChargingHistory()
      }
    },
    async refreshChargingHistory () {
      if (this.isNewRecord) {
        return
      }
      this.historyLoading = true
      try {
        const page = this.historyPagination.page || 1
        const rowsPerPage = this.historyPagination.rowsPerPage || 10
        const skip = (page - 1) * rowsPerPage
        const params = {
          id: this.chargerId,
          licensePlate: this.historyFilters.licensePlate || undefined,
          dateFrom: this.historyFilters.dateFrom || undefined,
          dateTo: this.historyFilters.dateTo || undefined,
          limit: rowsPerPage,
          skip,
          order: this.buildHistoryOrder()
        }
        const result = await Promise.all([
          getChargerChargingHistory(params),
          countChargerChargingHistory(params)
        ])
        this.historyRecords = result[0] || []
        this.historyTotalRecords = result[1] && result[1].count ? Number(result[1].count) : 0
      } catch (err) {
        addAlert({
          message: err && err.message ? err.message : 'Unable to load charging history',
          type: 'error'
        })
      } finally {
        this.historyLoading = false
      }
    },
    resetHistoryPageAndRefresh () {
      this.historyPagination.page = 1
      return this.refreshChargingHistory()
    },
    async loadRecord () {
      this.apiInProgress = true
      try {
        if (this.isNewRecord) {
          this.form = Object.assign({}, getOcppChargerTemplate())
          this.credentialForm.pushToCharger = false
          return
        }

        const charger = await getOcppCharger({ id: this.chargerId })
        this.form = Object.assign({}, getOcppChargerTemplate(), charger || {})
        this.credentialForm.pushToCharger = !!this.form.hasAuthToken
      } catch (err) {
        addAlert({
          message: err && err.message ? err.message : 'Unable to load charger',
          type: 'error'
        })
      } finally {
        this.apiInProgress = false
      }
    },
    getFormLocationSelection () {
      const latitude = this.form.latitude !== null && this.form.latitude !== undefined && this.form.latitude !== '' ? Number(this.form.latitude) : null
      const longitude = this.form.longitude !== null && this.form.longitude !== undefined && this.form.longitude !== '' ? Number(this.form.longitude) : null
      if (latitude === null || longitude === null || isNaN(latitude) || isNaN(longitude)) {
        return null
      }

      return {
        latitude,
        longitude,
        label: this.form.locationLabel || 'Current charger location',
        source: 'current'
      }
    },
    getLocationPickerMapboxInstance () {
      return this._locationPickerMapboxInstanceWrapper ? this._locationPickerMapboxInstanceWrapper.mapboxInstance : null
    },
    openLocationPicker () {
      if (!this.isAdmin || this.apiInProgress) {
        return
      }

      const currentSelection = this.getFormLocationSelection()
      this.locationPickerSelection = currentSelection
      this.locationPickerCenter = currentSelection ? [currentSelection.longitude, currentSelection.latitude] : null
      this.locationPickerDialog = true
      this.locationPickerMapShouldRender = true
      this.$nextTick(() => {
        setTimeout(() => {
          this.updateLocationPickerMapHeightOffset()
          this.resizeLocationPickerMap()
          this.renderLocationPickerMarker()
        }, 150)
      })
    },
    closeLocationPicker () {
      this.closeLocationPickerPopup()
      this.locationPickerDialog = false
    },
    onLocationPickerMapLoaded (mapboxInstance) {
      this._locationPickerMapboxInstanceWrapper = Object.freeze({ mapboxInstance })
      this.updateLocationPickerMapHeightOffset()
      this.resizeLocationPickerMap()
      this.renderLocationPickerMarker()
    },
    resizeLocationPickerMap () {
      const mapboxInstance = this.getLocationPickerMapboxInstance()
      window.dispatchEvent(new Event('resize'))
      if (mapboxInstance && mapboxInstance.currentMap && typeof mapboxInstance.currentMap.resize === 'function') {
        mapboxInstance.currentMap.resize()
      }
    },
    getRefHeight (refName, fallback) {
      const ref = this.$refs[refName]
      const element = ref && (ref.$el || ref)
      return element && element.clientHeight ? element.clientHeight : fallback
    },
    updateLocationPickerMapHeightOffset () {
      const viewportReserve = Math.ceil(window.innerHeight * 0.1)
      const toolbarHeight = this.getRefHeight('locationPickerToolbar', 48)
      const summaryHeight = this.getRefHeight('locationPickerSummary', 40)
      const actionsHeight = this.getRefHeight('locationPickerActions', 52)
      this.locationPickerMapHeightOffset = viewportReserve + toolbarHeight + summaryHeight + actionsHeight + 56
    },
    setLocationPickerSelection ({ lng, lat, label, source } = {}) {
      const longitude = Number(lng)
      const latitude = Number(lat)
      if (isNaN(latitude) || isNaN(longitude)) {
        return
      }

      this.closeLocationPickerPopup()
      this.locationPickerSelection = {
        latitude,
        longitude,
        label: label || '',
        source: source || 'mapClick'
      }
      this.locationPickerCenter = [longitude, latitude]
      this.renderLocationPickerMarker()
    },
    renderLocationPickerMarker () {
      const mapboxInstance = this.getLocationPickerMapboxInstance()
      const selection = this.locationPickerSelection
      if (!mapboxInstance || !selection) {
        return
      }

      mapboxInstance.setStartMarker({
        markerClassName: this.markerOptions.startMarkerClass,
        lng: selection.longitude,
        lat: selection.latitude
      })

      this.bindLocationPickerMarkerClick()
    },
    bindLocationPickerMarkerClick () {
      const mapboxInstance = this.getLocationPickerMapboxInstance()
      const marker = mapboxInstance && mapboxInstance.startEndMarkers ? mapboxInstance.startEndMarkers.start : null
      const markerElement = marker && typeof marker.getElement === 'function' ? marker.getElement() : null
      if (!markerElement) {
        return
      }

      markerElement.onclick = (event) => {
        if (event && typeof event.stopPropagation === 'function') {
          event.stopPropagation()
        }
        this.onLocationPickerMarkerClicked()
      }
    },
    closeLocationPickerPopup () {
      if (this._locationPickerPopup) {
        this._locationPickerPopup.remove()
        this._locationPickerPopup = null
      }
    },
    getReverseGeocodeCacheKey (selection) {
      return Number(selection.latitude).toFixed(6) + ',' + Number(selection.longitude).toFixed(6)
    },
    getCachedReverseGeocodeLabel (cacheKey) {
      const cached = reverseGeocodeCache[cacheKey]
      if (!cached) {
        return null
      }

      if (Date.now() - cached.timestamp > REVERSE_GEOCODE_CACHE_TTL_MS) {
        delete reverseGeocodeCache[cacheKey]
        return null
      }

      return cached.label
    },
    setCachedReverseGeocodeLabel (cacheKey, label) {
      reverseGeocodeCache[cacheKey] = {
        label,
        timestamp: Date.now()
      }
    },
    escapeHtml (value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },
    locationPickerPopupHtml (label, selection, loading) {
      const coordinatesLabel = Number(selection.latitude).toFixed(6) + ', ' + Number(selection.longitude).toFixed(6)
      return `
        <h4>${this.escapeHtml(label || 'Selected location')}</h4>
        <div><small>${this.escapeHtml(coordinatesLabel)}</small></div>
        ${loading ? '<div><small>Looking up address...</small></div>' : ''}
      `
    },
    getReverseGeocodeLabelFromResults (results) {
      if (!results || !results.length) {
        return 'Selected location'
      }
      return results[0].formatted_address || 'Selected location'
    },
    getBusStopReverseGeocodeLabel (busStop) {
      if (!busStop) {
        return null
      }
      return busStop.name || busStop.externalName || null
    },
    async getReverseGeocodeLabel (selection) {
      const cacheKey = this.getReverseGeocodeCacheKey(selection)
      const cachedLabel = this.getCachedReverseGeocodeLabel(cacheKey)
      if (cachedLabel) {
        return cachedLabel
      }

      const busStop = await reverseGeocodeBusStop({
        longitude: selection.longitude,
        latitude: selection.latitude,
        radius: BUS_STOP_REVERSE_GEOCODE_RADIUS_METERS
      })
      const busStopLabel = this.getBusStopReverseGeocodeLabel(busStop)
      if (busStopLabel) {
        this.setCachedReverseGeocodeLabel(cacheKey, busStopLabel)
        return busStopLabel
      }

      const results = await googleReverseGeocodeRequest({
        latitude: selection.latitude,
        longitude: selection.longitude
      })
      const label = this.getReverseGeocodeLabelFromResults(results)
      this.setCachedReverseGeocodeLabel(cacheKey, label)
      return label
    },
    async onLocationPickerMarkerClicked () {
      const selection = this.locationPickerSelection
      const mapboxInstance = this.getLocationPickerMapboxInstance()
      if (!selection || selection.source !== 'mapClick' || !mapboxInstance || !mapboxInstance.currentMap) {
        return
      }

      const cacheKey = this.getReverseGeocodeCacheKey(selection)
      const cachedLabel = this.getCachedReverseGeocodeLabel(cacheKey)
      this.closeLocationPickerPopup()
      this._locationPickerPopup = mapboxInstance.createPopup({
        className: 'st-map--popup',
        closeButton: true,
        closeOnClick: false,
        offset: 12
      })
      this._locationPickerPopup
        .setLngLat([selection.longitude, selection.latitude])
        .setHTML(this.locationPickerPopupHtml(cachedLabel || selection.label || 'Selected location', selection, !cachedLabel))
        .addTo(mapboxInstance.currentMap)

      if (cachedLabel) {
        this.locationPickerSelection = Object.assign({}, selection, { label: cachedLabel })
        return
      }

      try {
        const label = await this.getReverseGeocodeLabel(selection)
        const currentSelection = this.locationPickerSelection
        if (!currentSelection || currentSelection.source !== 'mapClick' || this.getReverseGeocodeCacheKey(currentSelection) !== cacheKey) {
          return
        }

        this.locationPickerSelection = Object.assign({}, currentSelection, { label })
        if (this._locationPickerPopup) {
          this._locationPickerPopup.setHTML(this.locationPickerPopupHtml(label, currentSelection, false))
        }
      } catch (err) {
        if (this._locationPickerPopup) {
          this._locationPickerPopup.setHTML(this.locationPickerPopupHtml('Selected location', selection, false))
        }
        addAlert({
          message: err && err.message ? err.message : 'Unable to reverse geocode selected location',
          type: 'warning'
        })
      }
    },
    onLocationPickerLocationSelected (location) {
      this.setLocationPickerSelection(location)
    },
    onLocationPickerMapClicked (location) {
      this.setLocationPickerSelection(Object.assign({}, location, {
        label: '',
        source: 'mapClick'
      }))
    },
    onLocationPickerBusStopClicked (feature) {
      if (!feature || !feature.geometry || !feature.geometry.coordinates || feature.geometry.coordinates.length < 2) {
        return
      }

      const coordinates = feature.geometry.coordinates.slice()
      const properties = Object.assign({}, feature.properties || {})
      this.setLocationPickerSelection({
        lng: coordinates[0],
        lat: coordinates[1],
        label: properties.name || properties.externalName || '',
        source: 'busStop'
      })
    },
    applyLocationPickerSelection () {
      const selection = this.locationPickerSelection
      if (!selection) {
        addAlert({ message: 'Select a location on the map before applying it', type: 'warning' })
        return
      }

      this.form.latitude = selection.latitude
      this.form.longitude = selection.longitude
      if (selection.label && (selection.source !== 'mapClick' || !this.form.locationLabel)) {
        this.form.locationLabel = selection.label
      }
      this.locationPickerDialog = false
    },
    buildLocationPayload (payload) {
      const hasLatitude = this.form.latitude !== null && this.form.latitude !== undefined && this.form.latitude !== ''
      const hasLongitude = this.form.longitude !== null && this.form.longitude !== undefined && this.form.longitude !== ''
      if (hasLatitude || hasLongitude) {
        if (!hasLatitude || !hasLongitude) {
          throw Object.assign(new Error('Latitude and longitude are both required when setting a charger location'), { status: 400 })
        }

        payload.latitude = Number(this.form.latitude)
        payload.longitude = Number(this.form.longitude)
        if (isNaN(payload.latitude) || isNaN(payload.longitude)) {
          throw Object.assign(new Error('Latitude and longitude must be numeric values'), { status: 400 })
        }
      }

      return payload
    },
    buildPayload () {
      const payload = {
        chargePointId: this.form.chargePointId ? this.form.chargePointId.trim() : '',
        status: this.form.status || 'Offline',
        locationLabel: this.form.locationLabel || null,
        timezone: this.form.timezone ? this.form.timezone.trim() : null
      }

      if (!payload.chargePointId) {
        throw Object.assign(new Error('chargePointId is required'), { status: 400 })
      }

      return this.buildLocationPayload(payload)
    },
    async saveRecord () {
      if (!this.isAdmin) {
        return
      }

      this.apiInProgress = true
      try {
        const payload = this.buildPayload()
        if (this.isNewRecord) {
          await createOcppCharger(payload)
          addAlert({
            message: 'EV charger created',
            type: 'success'
          })
          this.navigateToList({ reload: true })
        } else {
          payload.id = this.chargerId
          await updateOcppCharger(payload)
          addAlert({
            message: 'EV charger updated',
            type: 'success'
          })
          await this.loadRecord()
        }
      } catch (err) {
        addAlert({
          message: err && err.message ? err.message : 'Unable to save charger',
          type: 'error'
        })
      } finally {
        this.apiInProgress = false
      }
    },
    async deleteRecord () {
      if (!this.isAdmin || this.isNewRecord) {
        return
      }

      if (!confirm('Confirm deleting this EV charger?')) {
        return
      }

      this.apiInProgress = true
      try {
        await deleteOcppCharger({ id: this.chargerId })
        addAlert({
          message: 'EV charger deleted',
          type: 'success'
        })
        this.navigateToList({ reload: true })
      } catch (err) {
        addAlert({
          message: err && err.message ? err.message : 'Unable to delete charger',
          type: 'error'
        })
      } finally {
        this.apiInProgress = false
      }
    },
    async saveCredential () {
      if (!this.isAdmin || this.isNewRecord) {
        return
      }

      const password = this.credentialForm.password || ''
      const confirmPassword = this.credentialForm.confirmPassword || ''
      this.credentialForm.passwordTouched = true
      this.credentialForm.confirmPasswordTouched = true
      if (password.length < 8) {
        addAlert({ message: 'Password must be at least 8 characters', type: 'warning' })
        return
      }
      if (password !== confirmPassword) {
        addAlert({ message: 'Passwords do not match', type: 'warning' })
        return
      }

      this.apiInProgress = true
      try {
        const result = await setChargerCredential({
          id: this.chargerId,
          password,
          pushToCharger: !!this.credentialForm.pushToCharger
        })

        this.credentialForm.password = ''
        this.credentialForm.confirmPassword = ''
        this.credentialForm.showPassword = false
        this.credentialForm.passwordTouched = false
        this.credentialForm.confirmPasswordTouched = false
        this.credentialForm.mismatchAlertShown = false
        if (result && result.warning) {
          addAlert({ message: result.warning, type: 'warning' })
        } else {
          addAlert({ message: 'Credential updated', type: 'success' })
        }
        await this.loadRecord()
      } catch (err) {
        addAlert({
          message: err && err.message ? err.message : 'Unable to update credential',
          type: 'error'
        })
      } finally {
        this.apiInProgress = false
      }
    },
    onCredentialPasswordBlur () {
      this.credentialForm.passwordTouched = true
      this.validateCredentialMismatchOnBlur()
    },
    onCredentialConfirmPasswordBlur () {
      this.credentialForm.confirmPasswordTouched = true
      this.validateCredentialMismatchOnBlur()
    },
    validateCredentialMismatchOnBlur () {
      const password = this.credentialForm.password || ''
      const confirmPassword = this.credentialForm.confirmPassword || ''
      if (!password || !confirmPassword) {
        this.credentialForm.mismatchAlertShown = false
        return
      }
      if (password === confirmPassword) {
        this.credentialForm.mismatchAlertShown = false
        return
      }
      if (!this.credentialForm.mismatchAlertShown) {
        this.credentialForm.mismatchAlertShown = true
        addAlert({ message: 'Passwords do not match', type: 'warning' })
      }
    },
    async clearCredential () {
      if (!this.isAdmin || this.isNewRecord) {
        return
      }

      if (!confirm('Confirm clearing the charger credential?')) {
        return
      }

      this.apiInProgress = true
      try {
        await clearChargerCredential({ id: this.chargerId })
        addAlert({
          message: 'Credential cleared',
          type: 'success'
        })
        await this.loadRecord()
      } catch (err) {
        addAlert({
          message: err && err.message ? err.message : 'Unable to clear credential',
          type: 'error'
        })
      } finally {
        this.apiInProgress = false
      }
    },
    async populateDebugBootData () {
      if (!this.isAdmin || this.isNewRecord || !this.isStagingOrLocal) {
        return
      }

      this.apiInProgress = true
      try {
        await debugPopulateOcppChargerBootData({ id: this.chargerId })
        addAlert({
          message: 'Stub OCPP boot data populated',
          type: 'success'
        })
        await this.loadRecord()
      } catch (err) {
        addAlert({
          message: err && err.message ? err.message : 'Unable to populate stub data',
          type: 'error'
        })
      } finally {
        this.apiInProgress = false
      }
    },
    openEmulatorDialog () {
      if (!this.canUseEmulator || this.apiInProgress) {
        return
      }
      this.emulatorDialogVisible = true
    },
    navigateToList ({ reload } = {}) {
      const query = Object.assign({}, this.$route.query || {})
      if (reload) {
        query.refreshKey = String(Date.now())
      }
      this.$router.push({
        name: 'advanced-ev-chargers',
        query
      })
    }
  },
  beforeDestroy () {
    this.closeLocationPickerPopup()
    this._locationPickerMapboxInstanceWrapper = null
  }
}
